import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useOverlaySSE from './useOverlaySSE';
import useAlertAudio from './useAlertAudio';
import { formatRupiah } from '../../lib/api';
import './overlay.css';

const DEFAULT_SOUND = 'https://www.soundjay.com/buttons/sounds/button-09.mp3';

export default function OverlayAlert() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { playAlertSound, resumeAudio, isSuspended } = useAlertAudio();

  const anim = searchParams.get('anim') || 'slide';
  const duration = parseInt(searchParams.get('duration') || '5'); // Now expected in seconds for math
  const soundUrl = searchParams.get('sound'); // Optional override
  const accentColor = searchParams.get('accent') || '#FF6B35';
  const bgColor = searchParams.get('bg') || 'rgba(0,0,0,0.85)';
  const textColor = searchParams.get('text') || '#FFFFFF';
  const fontSize = searchParams.get('fontsize') || '32';
  const ttsEnabled = searchParams.get('tts') === 'true';
  const ttsVolume = parseFloat(searchParams.get('ttsvol') || '1.0');
  const ttsMin = parseInt(searchParams.get('ttsmin') || '0');
  const ttsVoice = searchParams.get('ttsvoice') || 'id-ID';

  const speak = useCallback((text) => {
    return new Promise((resolve) => {
      if (!ttsEnabled) {
        resolve();
        return;
      }
      
      // Attempt 1: Native Speech Synthesis (Fastest if available)
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        const voices = window.speechSynthesis.getVoices();
        const idVoice = voices.find(v => v.lang.startsWith('id') || v.name.toLowerCase().includes('indonesia'));
        
        if (idVoice && ttsVoice === 'id-ID') {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.volume = ttsVolume;
          utterance.voice = idVoice;
          utterance.lang = 'id-ID';
          utterance.onend = () => resolve();
          utterance.onerror = () => resolve();
          window.speechSynthesis.speak(utterance);
          return; 
        }
      }

      // Attempt 2: Same-Origin Backend Proxy (Ultimate Fallback for OBS)
      if (ttsVoice === 'id-ID') {
        const proxyUrl = `https://api.traktirkan.com/api/overlay/tts?text=${encodeURIComponent(text.substring(0, 200))}`;
        const audio = new Audio(proxyUrl);
        audio.volume = ttsVolume;
        audio.onended = () => resolve();
        audio.onerror = () => resolve();
        audio.play().catch(e => {
          console.warn("[AlertBox] Proxy TTS failed. Check OBS 'Control Audio' setting.", e);
          resolve();
        });
      } else {
        resolve();
      }
    });
  }, [ttsEnabled, ttsVolume, ttsVoice]);

  // Prime the voices
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  const [queue, setQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [exiting, setExiting] = useState(false);

  const runAlert = useCallback(async (data) => {
    setIsProcessing(true);
    setAlert(data);
    setExiting(false);

    // Play sound
    playAlertSound(soundUrl);

    // Start timing
    const startTime = Date.now();

    // TTS
    if (data.message && parseInt(data.amount || 0) >= ttsMin) {
      await new Promise(r => setTimeout(r, 200));
      await speak(`${data.supporter_name} mentraktir ${data.qty} ${data.unit_name}. Pesan: ${data.message}`);
    }

    // Wait for minimum duration
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(500, (duration * 1000) - elapsed);
    await new Promise(r => setTimeout(r, remaining));

    // Exit
    setExiting(true);
    await new Promise(r => setTimeout(r, 500)); // Out animation
    setAlert(null);
    setIsProcessing(false);
  }, [duration, playAlertSound, soundUrl, speak, ttsMin]);

  useEffect(() => {
    if (queue.length > 0 && !isProcessing) {
      const next = queue[0];
      setQueue(prev => prev.slice(1));
      runAlert(next);
    }
  }, [queue, isProcessing, runAlert]);

  const processedIds = useRef(new Set());

  const handleEvent = useCallback((event) => {
    console.log('[AlertBox] Event received:', event);
    if (!event.id) return;
    if (processedIds.current.has(event.id)) {
      console.log('[AlertBox] Duplicate event ignored:', event.id);
      return;
    }
    processedIds.current.add(event.id);
    
    // Keep set size reasonable
    if (processedIds.current.size > 100) {
      const first = processedIds.current.values().next().value;
      processedIds.current.delete(first);
    }

    if (event.type === 'donation') {
      console.log('[AlertBox] Adding donation to queue:', event);
      setQueue((prev) => [...prev, event]);
    }
  }, []);

  const { connected } = useOverlaySSE(token, handleEvent);

  const cssVars = {
    '--ov-accent': accentColor,
    '--ov-bg': bgColor,
    '--ov-text': textColor,
    '--ov-font-size': fontSize + 'px',
  };

  const enterClass = `alert-enter-${anim}`;
  const exitClass = `alert-exit-${anim}`;

  return (
    <div className="overlay-container" style={cssVars}>
      {/* Interaction button for browser audio policies */}
      {isSuspended && (
        <button 
          onClick={resumeAudio}
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            backgroundColor: accentColor,
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '50px',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            pointerEvents: 'auto'
          }}
        >
          🔊 Aktifkan Suara Alert
        </button>
      )}

      {/* Debug status dot (top right) */}
      <div style={{
        position: 'fixed',
        top: 10,
        right: 10,
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: connected ? '#22c55e' : '#ef4444',
        opacity: 0.5,
        zIndex: 999
      }} title={connected ? 'Connected' : 'Disconnected'} />
      
      {alert && (
        <div className={`alert-box ${exiting ? exitClass : enterClass}`}>
          <div className="alert-card">
            <div className={`alert-emoji ${!exiting ? 'alert-emoji-animate' : ''}`}>
              {alert.unit_emoji || '☕'}
            </div>
            <div className="alert-name">{alert.supporter_name}</div>
            <div className="alert-detail">
              mentraktir {alert.qty}× {alert.unit_name}
            </div>
            <div className="alert-amount" style={{ position: 'relative' }}>
              {formatRupiah(alert.amount)}
            </div>
            {alert.message && (
              <div className="alert-message">"{alert.message}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
